import type { ButtonHTMLAttributes } from 'react';
import { forwardRef, memo, useMemo } from 'react';
import type { Interpolation } from 'styled-components';

import type { TooltipProps } from 'components/ui/shared/Tooltip';

import Tooltip from 'components/ui/shared/Tooltip';
import { ElementTestId } from 'enums/testing';

import { ButtonContent, type ButtonSize, type ButtonVariant, StyledButton } from './Button.styles';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Optional CSS applied to the ButtonContent wrapper span
   */
  contentCss?: Interpolation<object>;
  /**
   * Optional boolean to force the button to be in an active state
   */
  forceActive?: boolean;
  /**
   * Optional CSS applied to the button element
   */
  iconButtonCss?: Interpolation<object>;
  /**
   * Button size
   */
  size?: ButtonSize;
  /**
   * Optional tooltip props if we want to show tooltip on hover
   */
  tooltip?: Omit<TooltipProps, 'children'>;
  /**
   * Button visual variant
   */
  variant?: ButtonVariant;
}

const defaultTooltipProps: Omit<TooltipProps, 'children'> = {
  content: null,
  placement: 'bottom-start',
  styleVariant: 'condensed',
};

const IconButton = forwardRef<HTMLButtonElement, Props>(
  ({ children, contentCss, forceActive, iconButtonCss, size = 'medium', title, tooltip, variant, ...props }, ref) => {
    const resolvedVariant: ButtonVariant = variant ?? 'secondary';

    const tooltipOptions = useMemo(
      () => (tooltip ? { ...defaultTooltipProps, ...tooltip } : { content: null }),
      [tooltip]
    );

    const button = (
      <StyledButton
        $active={forceActive}
        $buttonStyleOverride={iconButtonCss}
        $iconOnly={true}
        $size={size}
        $variant={resolvedVariant}
        aria-label={props['aria-label'] ?? title}
        aria-pressed={forceActive}
        data-icon-only={true}
        data-size={size}
        data-testid={ElementTestId.ICON_BUTTON}
        data-variant={resolvedVariant}
        ref={ref}
        title={title}
        type="button"
        {...props}
      >
        <ButtonContent $styleOverride={contentCss}>{children}</ButtonContent>
      </StyledButton>
    );

    return <Tooltip {...tooltipOptions}>{button}</Tooltip>;
  }
);

IconButton.displayName = 'IconButton';

export default memo(IconButton);
